/**
 * Service accounts - list. See frontend spec §5.2.
 *
 * Discovery and navigation first: click a row to open Service Detail.
 * Only compact credential actions (refresh / revoke) stay on the table.
 * Group assignment and enable/disable live on the detail page.
 */
import { el, mount } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import { renderLoadingState, renderErrorState, renderEmptyState, renderUnavailableState } from "../components/states";
import { renderStatusText } from "../components/statusText";
import { renderRelativeTime } from "../components/dateTime";
import { renderSecretReveal } from "../components/secretReveal";
import { confirmAction } from "../components/confirmDialog";
import { openModal } from "../components/modal";
import { showToast } from "../components/toast";
import { EMPTY, sortAccessGroups } from "../utils/format";
import { listServices, refreshServiceKey, revokeServiceKey } from "../services/admin";
import { ApiError } from "../services/apiClient";
import { restoreWindowScroll, stashServicesListState, takeServicesListState } from "../app/viewState";
import type { ServiceAccount } from "../types/api";

type ActiveFilter = "all" | "active" | "inactive";

export class ServiceAccountsPage {
  private body: HTMLElement = el("div", {}, [renderLoadingState("Loading service accounts…")]);
  private all: ServiceAccount[] = [];
  private loaded = false;
  private search = "";
  private filter: ActiveFilter = "all";
  private navigate: (path: string) => void = () => undefined;
  private segmentedEl!: HTMLElement;
  private pendingScrollY: number | null = null;
  private busyActions = new Map<number, string>();

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    this.navigate = navigate;

    const restored = takeServicesListState();
    if (restored) {
      this.search = restored.search;
      this.filter = restored.filter;
      this.pendingScrollY = restored.scrollY;
    }

    const searchInput = el("input", {
      className: "input input--sm",
      type: "text",
      placeholder: "Search by name…",
      value: this.search,
      oninput: (ev: Event) => {
        this.search = (ev.target as HTMLInputElement).value;
        this.renderList();
      },
    }) as HTMLInputElement;

    const segmented = el("div", { className: "segmented" }, [
      this.filterButton("all", "All"),
      this.filterButton("active", "Active"),
      this.filterButton("inactive", "Inactive"),
    ]);
    this.segmentedEl = segmented;
    this.syncSegmented();

    const toolbar = el(
      "div",
      { className: "toolbar toolbar--split" },
      [el("div", { className: "field--search field--search-grow" }, [searchInput]), segmented]
    );

    const registerButton = el(
      "button",
      {
        className: "btn btn-primary btn-sm",
        type: "button",
        onclick: () => this.navigate("/services/new"),
      },
      ["Register service"]
    );

    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "Service Accounts",
          subtitle: "Clients that pull data from Axis.",
          back: { fallbackPath: "/dashboard", navigate, preferHistory: false, label: "Back to Dashboard" },
          actions: [registerButton],
        }),
        el("div", { className: "panel panel--standard" }, [toolbar, this.body]),
      ])
    );

    void this.load();
  }

  destroy(): void {
    stashServicesListState({
      search: this.search,
      filter: this.filter,
      scrollY: window.scrollY,
    });
  }

  private filterButton(value: ActiveFilter, label: string): HTMLElement {
    const btn = el(
      "button",
      {
        className: "segmented__option",
        type: "button",
        "aria-pressed": String(this.filter === value),
        onclick: () => {
          this.filter = value;
          this.syncSegmented();
          this.renderList();
        },
      },
      [label]
    );
    btn.dataset.filterValue = value;
    return btn;
  }

  private syncSegmented(): void {
    for (const child of Array.from(this.segmentedEl.children)) {
      const option = child as HTMLElement;
      option.setAttribute("aria-pressed", String(option.dataset.filterValue === this.filter));
    }
  }

  private async load(): Promise<void> {
    try {
      this.all = await listServices();
      this.loaded = true;
      this.renderList();
    } catch (err) {
      this.loaded = false;
      if (err instanceof ApiError && (err.status === 401 || err.status === 503)) {
        mount(this.body, renderUnavailableState(err.message));
      } else {
        mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.load() }));
      }
    }
  }

  private renderList(): void {
    if (!this.loaded) return;

    const query = this.search.trim().toLowerCase();
    const filtered = this.all.filter((s) => {
      if (this.filter === "active" && !s.isActive) return false;
      if (this.filter === "inactive" && s.isActive) return false;
      if (query) {
        const haystack = s.name.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });

    if (!this.all.length) {
      mount(this.body, renderEmptyState("No service accounts yet."));
      return;
    }
    if (!filtered.length) {
      mount(this.body, renderEmptyState("No service accounts match these filters."));
      return;
    }

    mount(
      this.body,
      renderTable(filtered, this.busyActions, {
        view: (service) => this.navigate(`/services/${service.id}`),
        refreshKey: (service) => void this.refreshKey(service),
        revokeKey: (service) => void this.revokeKey(service),
      })
    );

    if (this.pendingScrollY !== null) {
      restoreWindowScroll(this.pendingScrollY);
      this.pendingScrollY = null;
    }
  }

  private async refreshKey(service: ServiceAccount): Promise<void> {
    const confirmed = await confirmAction({
      title: `Refresh API key for ${service.name}?`,
      message: "The current key will stop working immediately. The replacement key is shown only once.",
      confirmLabel: "Refresh API Key",
      danger: true,
    });
    if (!confirmed) return;

    await this.mutate(service, "Refreshing key…", async () => {
      const result = await refreshServiceKey(service.id);
      openModal(renderSecretReveal(result.apiKey, `New API key for "${service.name}"`), {
        title: "API key refreshed",
      });
      showToast("API key refreshed.", "success");
    });
  }

  private async revokeKey(service: ServiceAccount): Promise<void> {
    const confirmed = await confirmAction({
      title: `Revoke API key for ${service.name}?`,
      message:
        "The current key will stop working immediately. You can create a replacement later with Refresh API Key.",
      confirmLabel: "Revoke API Key",
      danger: true,
    });
    if (!confirmed) return;

    await this.mutate(service, "Revoking key…", async () => {
      await revokeServiceKey(service.id);
      showToast("API key revoked.", "success");
    });
  }

  private async mutate(service: ServiceAccount, label: string, action: () => Promise<void>): Promise<void> {
    if (this.busyActions.has(service.id)) return;
    this.busyActions.set(service.id, label);
    this.renderList();
    try {
      await action();
      await this.load();
    } catch (err) {
      showToast(errorMessage(err), "error");
    } finally {
      this.busyActions.delete(service.id);
      this.renderList();
    }
  }
}

interface RowActions {
  view: (service: ServiceAccount) => void;
  refreshKey: (service: ServiceAccount) => void;
  revokeKey: (service: ServiceAccount) => void;
}

function renderTable(rows: ServiceAccount[], busyActions: Map<number, string>, actions: RowActions): HTMLElement {
  const table = el("table", { className: "data-table" }, [
    el("thead", {}, [
      el("tr", {}, [
        el("th", { scope: "col" }, ["Name"]),
        el("th", { scope: "col", className: "data-table__col--status" }, ["Status"]),
        el("th", { scope: "col", className: "data-table__col--groups" }, ["Groups"]),
        el("th", { scope: "col", className: "data-table__col--key" }, ["API Key"]),
        el("th", { scope: "col", className: "data-table__col--relative" }, ["Created"]),
        el("th", { scope: "col", className: "data-table__col--relative" }, ["Updated"]),
        el("th", { scope: "col", className: "data-table__actions-col" }, ["Keys"]),
      ]),
    ]),
    el(
      "tbody",
      {},
      rows.map((s) => {
        const busyLabel = busyActions.get(s.id);
        const busy = Boolean(busyLabel);
        const open = () => {
          if (!busy) actions.view(s);
        };

        // No role="button" - rows contain credential icon buttons; role would nest interactives.
        return el(
          "tr",
          {
            className: `data-table__row--nav${busy ? " data-table__row--busy" : ""}`,
            tabIndex: busy ? -1 : 0,
            "aria-label": `Open ${s.name}`,
            "aria-disabled": busy ? "true" : undefined,
            onclick: open,
            onkeydown: (ev: KeyboardEvent) => {
              if (ev.key === "Enter" || ev.key === " ") {
                ev.preventDefault();
                open();
              }
            },
          },
          [
            el("td", {}, [s.name]),
            el("td", {}, [
              renderStatusText(s.isActive ? "Active" : "Inactive", s.isActive ? "success" : "muted"),
            ]),
            el("td", {}, [groupsSummary(s)]),
            el("td", {}, [
              renderStatusText(s.hasApiKey ? "Present" : "Revoked", s.hasApiKey ? "success" : "muted"),
            ]),
            el("td", {}, [renderRelativeTime(s.createdAt)]),
            el("td", {}, [renderRelativeTime(s.updatedAt)]),
            el(
              "td",
              {
                className: "data-table__actions-cell",
                onclick: (ev: MouseEvent) => ev.stopPropagation(),
                onkeydown: (ev: KeyboardEvent) => ev.stopPropagation(),
              },
              [
                el("div", { className: "row-actions" }, [
                  iconAction({
                    label: `Refresh API key for ${s.name}`,
                    title: "Refresh API Key",
                    symbol: "↻",
                    disabled: busy,
                    onClick: () => actions.refreshKey(s),
                  }),
                  iconAction({
                    label: `Revoke API key for ${s.name}`,
                    title: "Revoke API Key",
                    symbol: "⊘",
                    disabled: busy || !s.hasApiKey,
                    danger: true,
                    onClick: () => actions.revokeKey(s),
                  }),
                  busyLabel ? el("span", { className: "data-table__cell-secondary" }, [busyLabel]) : null,
                ]),
              ]
            ),
          ]
        );
      })
    ),
  ]);

  return el("div", { className: "data-table-wrap" }, [table]);
}

function iconAction(opts: {
  label: string;
  title: string;
  symbol: string;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
}): HTMLElement {
  return el(
    "button",
    {
      className: `row-actions__icon${opts.danger ? " row-actions__icon--danger" : ""}`,
      type: "button",
      title: opts.title,
      "aria-label": opts.label,
      disabled: opts.disabled,
      onclick: (ev: MouseEvent) => {
        ev.stopPropagation();
        opts.onClick();
      },
    },
    [opts.symbol]
  );
}

function groupsSummary(s: ServiceAccount): string {
  if (!s.groups.length) return EMPTY;
  return sortAccessGroups(s.groups)
    .map((g) => g.name)
    .join(", ");
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong loading this data.";
}
